// utils/Logger.ts

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogPayload {
  message?: string;
  context?: any;
  level?: LogLevel;
}

const RESET = '\x1b[0m';

export class Logger {
  private context: string = '';

  // init with optional string for [context]
  constructor(context?: string) {
    if (context) {
      this.context = context;
    }
  }

  private format(payload: LogPayload, level: LogLevel): string {
    const timestamp = new Date().toISOString();

    const parts: string[] = [];
    const formattedContext = this.context ? `[${this.context}]` : '';

    if (payload.message) {
      parts.push(payload.message);
    }

    if (payload.context) {
      try {
        parts.push(JSON.stringify(payload.context));
      } catch {
        parts.push(payload.context);
      }
    }

    const formatted = parts.join(' | ');
    return `[${level.toUpperCase()}] ${formattedContext} ${timestamp} ${formatted}${RESET}`;
  }

  public log(input: string | LogPayload) {
    this.write(input, 'info');
  }

  public warn(input: string | LogPayload) {
    this.write(input, 'warn');
  }

  public error(input: string | LogPayload) {
    this.write(input, 'error');
  }

  public debug(input: string | LogPayload) {
    this.write(input, 'debug');
  }

  private write(input: string | LogPayload, defaultLevel: LogLevel) {
    let payload: LogPayload;

    if (typeof input === 'string') {
      payload = { message: input, level: defaultLevel };
    } else {
      payload = { ...input };
      payload.level = payload.level || defaultLevel;
    }

    const formatted = this.format(payload, payload.level!);

    switch (payload.level) {
      case 'error':
        console.error(formatted);
        break;
      case 'warn':
        console.warn(formatted);
        break;
      case 'debug':
        console.debug(formatted);
        break;
      default:
        console.log(formatted);
        break;
    }
  }
}
